import React, { useState, useEffect, useCallback, useRef } from 'react';
import PreviewRecordSelector from './PreviewRecordSelector';

const PreviewFullscreenModal = ({
  isOpen,
  onClose,
  htmlContent,
  emailType,
  requiredContexts = [],
  initialCompanyId = null,
  initialEventId = null,
  initialUserId = null
}) => {
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewErrors, setPreviewErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(initialCompanyId);
  const [selectedEventId, setSelectedEventId] = useState(initialEventId);
  const [selectedUserId, setSelectedUserId] = useState(initialUserId);
  const [dataUsed, setDataUsed] = useState(null);
  const [iframeHeight, setIframeHeight] = useState(800);
  const iframeRef = useRef(null);

  // Sync initial values when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCompanyId(initialCompanyId);
      setSelectedEventId(initialEventId);
      setSelectedUserId(initialUserId);
    }
  }, [isOpen, initialCompanyId, initialEventId, initialUserId]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const loadPreview = useCallback(async () => {
    if (!htmlContent) {
      setPreviewHtml('');
      setPreviewErrors([]);
      return;
    }

    setLoading(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch('/admin/email_templates/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          html_content: htmlContent,
          email_type: emailType,
          company_id: selectedCompanyId,
          event_id: selectedEventId,
          user_id: selectedUserId
        })
      });

      const data = await response.json();
      setPreviewHtml(data.html || '');
      setPreviewErrors(data.errors || []);
      setDataUsed(data.data_used);
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreviewErrors(['Failed to load preview']);
    } finally {
      setLoading(false);
    }
  }, [htmlContent, emailType, selectedCompanyId, selectedEventId, selectedUserId]);

  // Load preview when modal opens or selections change
  useEffect(() => {
    if (isOpen) {
      loadPreview();
    }
  }, [isOpen, selectedCompanyId, selectedEventId, selectedUserId, htmlContent]);

  const handleSelectionChange = ({ companyId, eventId, userId }) => {
    setSelectedCompanyId(companyId);
    setSelectedEventId(eventId);
    setSelectedUserId(userId);
  };

  // Resize iframe to match its content height (eliminates internal scrollbar)
  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentDocument) {
      const body = iframe.contentDocument.body;
      if (body) {
        // Get the full content height
        const contentHeight = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          iframe.contentDocument.documentElement.scrollHeight
        );
        setIframeHeight(contentHeight);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="preview-fullscreen-modal" onClick={onClose}>
      <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="fullscreen-header">
          <h5 className="fullscreen-title">
            <i className="fa fa-envelope mr-2"></i>
            Email Preview
          </h5>
          <button className="btn btn-light close-btn" onClick={onClose}>
            <i className="fa fa-times mr-1"></i>
            Close
            <span className="keyboard-hint ml-2">ESC</span>
          </button>
        </div>

        {/* Controls bar */}
        <div className="fullscreen-controls">
          <div className="controls-left">
            <PreviewRecordSelector
              requiredContexts={requiredContexts}
              onSelectionChange={handleSelectionChange}
              selectedCompanyId={selectedCompanyId}
              selectedEventId={selectedEventId}
              selectedUserId={selectedUserId}
            />
          </div>
          <div className="controls-right">
            {dataUsed && (
              <div className="data-used-badge">
                <small className="text-muted mr-2">Using:</small>
                {dataUsed.company && (
                  <span className="badge badge-light mr-1">{dataUsed.company.name}</span>
                )}
                {dataUsed.event && (
                  <span className="badge badge-light mr-1">{dataUsed.event.name}</span>
                )}
                {dataUsed.user && (
                  <span className="badge badge-light">
                    {dataUsed.user.first_name} {dataUsed.user.last_name}
                  </span>
                )}
              </div>
            )}
            <button
              className="btn btn-sm btn-outline-secondary ml-3"
              onClick={loadPreview}
              disabled={loading}
            >
              <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {/* Errors */}
        {previewErrors.length > 0 && (
          <div className="fullscreen-errors">
            {previewErrors.map((error, idx) => (
              <div key={idx} className="alert alert-danger alert-sm mb-0 mr-2">
                <i className="fa fa-exclamation-triangle mr-1"></i>
                {error}
              </div>
            ))}
          </div>
        )}

        {/* Preview area */}
        <div className="fullscreen-preview-area">
          {loading ? (
            <div className="preview-loading">
              <i className="fa fa-spinner fa-spin fa-3x"></i>
              <p className="mt-3">Loading preview...</p>
            </div>
          ) : previewHtml ? (
            <div className="email-container">
              <iframe
                ref={iframeRef}
                srcDoc={previewHtml}
                title="Email Preview"
                className="fullscreen-iframe"
                style={{ height: `${iframeHeight}px` }}
                sandbox="allow-same-origin"
                onLoad={handleIframeLoad}
              />
            </div>
          ) : (
            <div className="preview-empty">
              <i className="fa fa-file-alt fa-4x text-muted"></i>
              <p className="text-muted mt-3">No HTML content to preview</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .preview-fullscreen-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1100;
          background: rgba(0, 0, 0, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .fullscreen-content {
          width: 100%;
          height: 100%;
          background: #1a1a1a;
          display: flex;
          flex-direction: column;
        }

        .fullscreen-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 24px;
          background: #2d2d2d;
          border-bottom: 1px solid #444;
        }

        .fullscreen-title {
          margin: 0;
          color: white;
          font-size: 18px;
        }

        .close-btn {
          display: flex;
          align-items: center;
        }

        .keyboard-hint {
          font-size: 11px;
          padding: 2px 6px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 3px;
          color: #666;
        }

        .fullscreen-controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 24px;
          background: #2d2d2d;
          border-bottom: 1px solid #444;
        }

        .controls-left {
          display: flex;
          align-items: center;
        }

        .controls-right {
          display: flex;
          align-items: center;
        }

        .data-used-badge {
          display: flex;
          align-items: center;
        }

        .fullscreen-errors {
          display: flex;
          padding: 12px 24px;
          background: #fff5f5;
        }

        .fullscreen-preview-area {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          background: #1a1a1a;
        }

        .preview-loading,
        .preview-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #888;
          min-height: 100%;
        }

        .email-container {
          max-width: 650px;
          margin: 0 auto;
          background: white;
          box-shadow: 0 0 40px rgba(0, 0, 0, 0.5);
        }

        .fullscreen-iframe {
          width: 100%;
          display: block;
          border: none;
          background: white;
        }

        /* Override PreviewRecordSelector styles for dark theme */
        .fullscreen-controls .preview-record-selector {
          background: transparent;
          border: none;
          padding: 0;
        }

        .fullscreen-controls .preview-record-selector .form-group {
          margin-bottom: 0;
        }

        .fullscreen-controls .preview-record-selector label {
          color: #ccc;
        }

        @media (max-width: 768px) {
          .fullscreen-controls {
            flex-direction: column;
            gap: 12px;
          }

          .controls-left,
          .controls-right {
            width: 100%;
            justify-content: center;
          }

          .email-container {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default PreviewFullscreenModal;
